import { useMemo, useState } from "react";
import PageHeader from "@/components/feature/PageHeader";
import FilterGroup from "@/components/base/FilterGroup";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { getAllNews } from "@/lib/content";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const TIME_OPTIONS = [
  { label: "最新", value: "all" },
  { label: "过去一周", value: "week" },
  { label: "过去一个月", value: "month" },
];

const TYPE_OPTIONS = [
  { label: "全部", value: "all" },
  { label: "电影", value: "电影" },
  { label: "电视剧", value: "电视剧" },
  { label: "综艺", value: "综艺" },
];

const PAGE_SIZE = 5;
const TODAY = "2026-10-15";
const WEEK_AGO = "2026-10-08";
const MONTH_AGO = "2026-09-15";

export default function NewsCategoryPage() {
  const [time, setTime] = useState("all");
  const [type, setType] = useState("all");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let list = [...getAllNews()].sort((a, b) => (a.date < b.date ? 1 : -1));
    if (time === "week") list = list.filter((n) => n.date >= WEEK_AGO && n.date <= TODAY);
    if (time === "month") list = list.filter((n) => n.date >= MONTH_AGO && n.date <= TODAY);
    if (type !== "all") list = list.filter((n) => n.type === type);
    return list;
  }, [time, type]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleTime = (v: string) => {
    setTime(v);
    setPage(1);
  };
  const handleType = (v: string) => {
    setType(v);
    setPage(1);
  };
  const reset = () => {
    setTime("all");
    setType("all");
    setPage(1);
  };

  useDocumentTitle(
    "影视资讯 - 黑皮辣妹",
    "黑皮辣妹影视资讯频道，汇总最新影视动态、定档消息与行业观察，及时了解影视资讯。",
  );

  return (
    <div className="hp-fade-up">
      <PageHeader
        title="最新影视资讯"
        description="汇总影视行业最新动态、定档消息、幕后花絮与市场观察，按发布时间为你及时呈现。"
        breadcrumb={[{ label: "影视资讯" }]}
        eyebrow="News"
      />

      <section className="w-full px-4 md:px-6">
        <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-6 py-2 mb-6">
          <FilterGroup label="时间" options={TIME_OPTIONS} value={time} onChange={handleTime} />
          <FilterGroup label="类型" options={TYPE_OPTIONS} value={type} onChange={handleType} />
        </div>

        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-foreground-500">
            共找到 <span className="text-primary-600 font-medium">{filtered.length}</span> 条资讯
          </p>
        </div>

        {pageItems.length > 0 ? (
          <div className="space-y-3">
            {pageItems.map((item) => (
              <NewsCard key={item.id} item={item} layout="row" />
            ))}
          </div>
        ) : (
          <EmptyState
            title="暂无相关影视资讯"
            description="当前筛选条件下没有匹配的资讯，您可以重置筛选条件后再试。"
            actionLabel="重置筛选条件"
            onAction={reset}
          />
        )}

        {filtered.length > 0 && (
          <Pagination current={currentPage} total={totalPages} onChange={setPage} />
        )}
      </section>
    </div>
  );
}